import React, { useState, useEffect, useRef } from "react";
import { getBackendBaseUrl } from "../../utils/apiUrl";
import { FiMic, FiMicOff, FiMinimize, FiMaximize, FiPhoneOff, FiTv } from "react-icons/fi";

export default function VideoSession({
  conversationUrl,
  conversationId,
  replicaName = "Ava",
  onEnd,
  onTranscriptionEvent,
  onVoiceMessageSenderReady,
  isMiniPlayer = false,
  onToggleMiniPlayer,
  isAvatarCentered = false,
  onToggleAvatarCenter,
  applyGreenscreen = false,
  avatarBackground = "",
  avatarBackgroundSize = "cover",
  avatarZoom = 1,
  avatarTranslateX = 0,
  avatarTranslateY = 0,
  joiningText = "Joining Voice Session...",
  aspectRatio = undefined
}) {
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [avatarState, setAvatarState] = useState("listening"); // 'speaking' | 'listening' | 'thinking'
  const [isHovered, setIsHovered] = useState(false);

  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const localVideoRef = useRef(null); // Local user camera preview
  const callFrameRef = useRef(null);
  const canvasRef = useRef(null);
  const handleTavusEventRef = useRef(null);
  const onVoiceMessageSenderReadyRef = useRef(onVoiceMessageSenderReady);

  const containerRef = useRef(null);
  const [scaleFactor, setScaleFactor] = useState(1);

  useEffect(() => {
    if (!containerRef.current) return;
    const updateScale = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        setScaleFactor(width / 400); // 400 is the base widget width
      }
    };
    updateScale();
    window.addEventListener("resize", updateScale);
    const timeout = setTimeout(updateScale, 100);
    const interval = setInterval(updateScale, 1000);
    return () => {
      window.removeEventListener("resize", updateScale);
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [isAvatarCentered]);

  const resolvedTranslateX = avatarTranslateX * scaleFactor;
  const resolvedTranslateY = avatarTranslateY * scaleFactor;

  useEffect(() => {
    onVoiceMessageSenderReadyRef.current = onVoiceMessageSenderReady;
  }, [onVoiceMessageSenderReady]);

  // Sync transcription event handlers to avoid closures
  useEffect(() => {
    handleTavusEventRef.current = (rawData) => {
      let data = rawData;
      if (typeof rawData === "string") {
        try { data = JSON.parse(rawData); } catch { return; }
      }
      if (!data) return;

      const eventType = data.event_type || data.eventType || data.tool_name || (data.msg && data.msg.action);
      const args = data.properties || data.args || (data.msg && data.msg.args) || data.data;

      console.log("[Tavus Voice Mode Event]", eventType, args);

      // Intercept tool calls for real-time lead capture
      if (eventType === "conversation.tool_call" || eventType === "tool_call") {
        console.log("🛠️ [Tavus Tool Call Intercepted]", args);
        const toolName = args.name || args.tool_name;
        if (toolName === "capture_lead") {
          let toolArgs = args.arguments || {};
          if (typeof toolArgs === "string") {
            try { toolArgs = JSON.parse(toolArgs); } catch (e) { console.error("Failed to parse tool arguments string", e); }
          }
          console.log("📦 [Tavus Tool Call Arguments]", toolArgs);

          const leadPayload = {
            name: toolArgs.name || "",
            email: toolArgs.email || "",
            phone: toolArgs.phone || "",
            reason: toolArgs.reason || "Real-time voice assistant session tool call",
            agentId: replicaName || "Ava"
          };

          const apiBase = getBackendBaseUrl() + "/chatbot";
          console.log("📨 [Tavus Tool Call] Sending lead to backend:", `${apiBase}/leads`, leadPayload);

          fetch(`${apiBase}/leads`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(leadPayload)
          }).then(res => {
            if (res.ok) {
              console.log("🎉 [Tavus Tool Call] Saved lead successfully!");

              // Echo back to Tavus so LLM knows the lead is saved
              if (callFrameRef.current) {
                console.log("💬 [Tavus Tool Call] Echoing success back to Tavus");
                callFrameRef.current.sendAppMessage({
                  message_type: 'conversation',
                  event_type: 'conversation.echo',
                  conversation_id: conversationId,
                  properties: {
                    modality: 'text',
                    text: 'The lead details for ' + leadPayload.name + ' have been successfully saved to the database. You can confirm this to the user.',
                    done: true
                  }
                }, '*');
              }
            } else {
              console.error("❌ [Tavus Tool Call] Failed to save lead, status:", res.status);
            }
          }).catch(err => {
            console.error("❌ [Tavus Tool Call] Network error saving lead:", err);
          });
        }

        if (toolName === "show_link") {
          let toolArgs = args.arguments || {};
          if (typeof toolArgs === "string") {
            try { toolArgs = JSON.parse(toolArgs); } catch (e) { console.error("Failed to parse tool arguments string", e); }
          }
          console.log("🔗 [Tavus Tool Call show_link Arguments]", toolArgs);

          if (toolArgs.url) {
            // Trigger transcription callback to print the link in the chat bubble
            if (onTranscriptionEvent) {
              const displayTitle = toolArgs.title || "View Page Link";
              const formattedText = `I've shared the link in the chat:\n[${displayTitle}](${toolArgs.url})`;
              onTranscriptionEvent("replica", formattedText, true);
            }

            // Echo back to Tavus so LLM knows the link is displayed successfully
            if (callFrameRef.current) {
              console.log("💬 [Tavus Tool Call show_link] Echoing success back to Tavus");
              callFrameRef.current.sendAppMessage({
                message_type: 'conversation',
                event_type: 'conversation.echo',
                conversation_id: conversationId,
                properties: {
                  modality: 'text',
                  text: 'The link was successfully displayed to the user in their chat window.',
                  done: true
                }
              }, '*');
            }
          }
        }
      }

      // Handle transcription events
      if (
        eventType === "conversation.transcription" ||
        eventType === "transcription" ||
        eventType === "conversation.utterance.streaming" ||
        eventType === "utterance.streaming"
      ) {
        const speaker = (args && (args.speaker || args.role)) || (data.properties && (data.properties.speaker || data.properties.role)) || "replica";
        const text = (args && (args.text || args.speech)) || "";
        const speakerKey = speaker === "replica" || speaker === "agent" || speaker === "ai" ? "replica" : "user";
        const isFinal = args && (args.final !== undefined ? args.final : false);
        if (text && onTranscriptionEvent) {
          onTranscriptionEvent(speakerKey, text, isFinal);
        }
      }

      if (eventType === "conversation.utterance" || eventType === "utterance") {
        const speaker = (args && (args.role || args.speaker)) || (data.properties && (data.properties.role || data.properties.speaker)) || "replica";
        const text = (args && (args.speech || args.text)) || "";
        const speakerKey = speaker === "replica" || speaker === "agent" || speaker === "ai" ? "replica" : "user";
        if (text && onTranscriptionEvent) {
          onTranscriptionEvent(speakerKey, text, true);
        }
      }

      // Handle speaking states
      if (
        eventType === "conversation.replica_started_speaking" ||
        eventType === "replica_started_speaking" ||
        eventType === "conversation.started_speaking" ||
        eventType === "started_speaking"
      ) {
        const speaker = args && (args.role || args.speaker);
        if (!speaker || speaker === "replica" || speaker === "agent" || speaker === "ai") {
          setAvatarState("speaking");
        }
      }

      if (
        eventType === "conversation.replica_stopped_speaking" ||
        eventType === "replica_stopped_speaking" ||
        eventType === "conversation.stopped_speaking" ||
        eventType === "stopped_speaking"
      ) {
        const speaker = args && (args.role || args.speaker);
        if (!speaker || speaker === "replica" || speaker === "agent" || speaker === "ai") {
          setAvatarState("listening");
          if (onTranscriptionEvent) {
            onTranscriptionEvent("replica", "", true);
          }
        }
      }
    };
  }, [onTranscriptionEvent]);

  const applyChromaKeyEffect = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext("2d");

    let active = true;
    const chromaKey = () => {
      if (!active || !video || video.paused || video.ended) {
        if (active) requestAnimationFrame(chromaKey);
        return;
      }

      // Avoid rendering errors if video dimensions aren't available yet
      if (!video.videoWidth || !video.videoHeight) {
        requestAnimationFrame(chromaKey);
        return;
      }

      const targetWidth = Math.min(480, video.videoWidth);
      const scale = targetWidth / video.videoWidth;
      canvas.width = targetWidth;
      canvas.height = Math.round(video.videoHeight * scale);

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      try {
        const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = frame.data;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          // Professional difference-based green screen keying
          if (g > 90 && g > r * 1.4 && g > b * 1.4) {
            data[i + 3] = 0; // Make transparent
          }
        }
        ctx.putImageData(frame, 0, 0);
      } catch (e) {
        // Cross-origin safe fallback
      }
      requestAnimationFrame(chromaKey);
    };

    const handlePlay = () => {
      chromaKey();
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("playing", handlePlay);
    video.addEventListener("timeupdate", handlePlay);
    video.addEventListener("loadedmetadata", handlePlay);

    if (!video.paused) {
      chromaKey();
    }

    return () => {
      active = false;
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("playing", handlePlay);
      video.removeEventListener("timeupdate", handlePlay);
      video.removeEventListener("loadedmetadata", handlePlay);
    };
  };

  useEffect(() => {
    if (!loading && applyGreenscreen) {
      const cleanup = applyChromaKeyEffect();
      return cleanup;
    }
  }, [loading, applyGreenscreen]);

  // Connect to Daily Call
  useEffect(() => {
    let callObject = null;
    let intervalId = null;
    let isCleanedUp = false;

    const init = async () => {
      if (isCleanedUp) return;
      if (!window.Daily) return;

      // Destroy any existing Daily call frames first to prevent "Duplicate DailyIframe instances are not allowed"
      const existingInstance = window.Daily.getCallInstance();
      if (existingInstance) {
        console.log("[Daily] Destroying existing Daily call instance to prevent duplication");
        try {
          await existingInstance.destroy();
          await new Promise((resolve) => setTimeout(resolve, 200));
        } catch (e) {
          console.warn("[Daily] Error destroying existing instance:", e);
        }
      }

      if (isCleanedUp) return;

      // Enable both mic and camera inputs so raven-1 perception layer is functional
      callObject = window.Daily.createCallObject({
        audioSource: true,
        videoSource: true, // Opened camera so avatar can see you!
      });
      callFrameRef.current = callObject;

      // Bind the chat message sender callback immediately
      if (onVoiceMessageSenderReadyRef.current) {
        onVoiceMessageSenderReadyRef.current((text) => {
          if (callFrameRef.current) {
            const state = callFrameRef.current.meetingState();
            if (state !== "joined-meeting") {
              console.warn("[Tavus voice echo send] Delayed send: not joined yet. Current state:", state);
              return;
            }
            console.log("[Tavus voice echo send]", text);
            callFrameRef.current.sendAppMessage({
              message_type: "conversation",
              event_type: "conversation.respond",
              conversation_id: conversationId,
              properties: { text },
            }, '*');
          }
        });
      }

      // Robust track sync helper to bypass race conditions
      const syncTracks = () => {
        if (!callFrameRef.current) return;
        const call = callFrameRef.current;
        const participants = call.participants();
        if (!participants) return;

        let hasRemoteVideo = false;

        Object.entries(participants).forEach(([id, p]) => {
          const isLocal = p.local || id === "local" || p.session_id === "local";

          if (isLocal) {
            const videoTrack = p.tracks?.video?.track;
            if (videoTrack && localVideoRef.current) {
              const currentStream = localVideoRef.current.srcObject;
              const hasTrackChanged = !currentStream ||
                typeof currentStream.getVideoTracks !== "function" ||
                currentStream.getVideoTracks()[0] !== videoTrack;
              if (hasTrackChanged) {
                console.log("[Daily Sync] Setting local user camera track", videoTrack.id);
                localVideoRef.current.srcObject = new MediaStream([videoTrack]);
                localVideoRef.current.play().catch((e) => console.warn("localVideo play error", e));
              }
            } else if (!videoTrack && localVideoRef.current) {
              localVideoRef.current.srcObject = null;
            }
            return;
          }

          // Remote replica
          const videoTrack = p.tracks?.video?.track;
          if (videoTrack && videoRef.current) {
            hasRemoteVideo = true;
            const currentStream = videoRef.current.srcObject;
            const hasTrackChanged = !currentStream ||
              typeof currentStream.getVideoTracks !== "function" ||
              currentStream.getVideoTracks()[0] !== videoTrack;
            if (hasTrackChanged) {
              console.log("[Daily Sync] Setting remote replica avatar video track", videoTrack.id);
              videoRef.current.srcObject = new MediaStream([videoTrack]);
              setLoading(false); // Trigger load completion when video stream is attached
              videoRef.current.play().catch((e) => console.warn("video play error", e));
            }
          }

          const audioTrack = p.tracks?.audio?.track;
          if (audioTrack && audioRef.current) {
            const currentStream = audioRef.current.srcObject;
            const hasTrackChanged = !currentStream ||
              typeof currentStream.getAudioTracks !== "function" ||
              currentStream.getAudioTracks()[0] !== audioTrack;
            if (hasTrackChanged) {
              console.log("[Daily Sync] Setting remote replica avatar audio track", audioTrack.id);
              audioRef.current.srcObject = new MediaStream([audioTrack]);
              audioRef.current.play().catch((e) => console.warn("audio play error", e));
            }
          }
        });

        // Only clear remote video if there are no remote participants in the call at all
        const remoteParticipants = Object.entries(participants).filter(([id, p]) => {
          return !(p.local || id === "local" || p.session_id === "local");
        });
        if (remoteParticipants.length === 0 && videoRef.current && videoRef.current.srcObject) {
          console.log("[Daily Sync] Clearing remote replica avatar video track");
          videoRef.current.srcObject = null;
        }
      };

      // Register listeners BEFORE joining to prevent missing initial tracks
      callObject.on("track-started", syncTracks);
      callObject.on("track-stopped", syncTracks);
      callObject.on("participant-joined", syncTracks);
      callObject.on("participant-updated", syncTracks);
      callObject.on("joined-meeting", syncTracks);

      callObject.on("app-message", (event) => {
        console.log("DAILY CALLFRAME APP MESSAGE EVENT RECEIVED:", event);
        if (event && handleTavusEventRef.current) {
          handleTavusEventRef.current(event.data || event);
        }
      });

      // Join the conversation session
      callObject.join({ url: conversationUrl }).then((initialParticipants) => {
        console.log("[Daily] Joined conversation room successfully, clearing initial loader", initialParticipants);
        syncTracks();
        setLoading(false);
      }).catch((err) => {
        console.error("[Daily] Join failed:", err);
      });

      intervalId = setInterval(syncTracks, 500);
    };

    const handleUnload = () => {
      if (callFrameRef.current) {
        try {
          callFrameRef.current.leave();
          callFrameRef.current.destroy();
        } catch (e) { }
      }
      if (conversationId) {
        const baseUrl = getBackendBaseUrl();
        navigator.sendBeacon(`${baseUrl}/chatbot/conversations/${conversationId}/end`);
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);

    if (!window.Daily) {
      let script = document.querySelector('script[src*="daily-js"]');
      if (!script) {
        script = document.createElement("script");
        script.src = "https://unpkg.com/@daily-co/daily-js";
        script.async = true;
        document.body.appendChild(script);
      }
      const onLoad = () => {
        init();
      };
      script.addEventListener("load", onLoad);

      return () => {
        isCleanedUp = true;
        if (intervalId) clearInterval(intervalId);
        script.removeEventListener("load", onLoad);
        window.removeEventListener("beforeunload", handleUnload);
        window.removeEventListener("pagehide", handleUnload);
        if (callObject) {
          try {
            callObject.leave();
            callObject.destroy();
          } catch (e) { }
        }
        if (callFrameRef.current) {
          try {
            callFrameRef.current.leave();
            callFrameRef.current.destroy();
          } catch (e) { }
          callFrameRef.current = null;
        }
      };
    } else {
      init();
      return () => {
        isCleanedUp = true;
        if (intervalId) clearInterval(intervalId);
        window.removeEventListener("beforeunload", handleUnload);
        window.removeEventListener("pagehide", handleUnload);
        if (callObject) {
          try {
            callObject.leave();
            callObject.destroy();
          } catch (e) { }
        }
        if (callFrameRef.current) {
          try {
            callFrameRef.current.leave();
            callFrameRef.current.destroy();
          } catch (e) { }
          callFrameRef.current = null;
        }
      };
    }
  }, [conversationUrl, conversationId]);

  const handleToggleMute = () => {
    if (callFrameRef.current) {
      const newMute = !isMuted;
      callFrameRef.current.setLocalAudio(!newMute);
      setIsMuted(newMute);
    }
  };

  const handleTogglePause = () => {
    const videoEl = videoRef.current;
    if (videoEl) {
      if (isPaused) {
        videoEl.play().catch(() => { });
      } else {
        videoEl.pause();
      }
      setIsPaused(!isPaused);
    }
  };

  // Helper to resolve background style (supports solid color, gradient, image url, gif url)
  const getContainerBackgroundStyle = () => {
    if (!avatarBackground) {
      return { background: "#020617" };
    }
    const isUrl = avatarBackground.startsWith("http") || avatarBackground.startsWith("/") || avatarBackground.startsWith("data:");
    if (isUrl) {
      return {
        backgroundImage: `url(${avatarBackground})`,
        backgroundSize: avatarBackgroundSize,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat"
      };
    }
    return {
      background: avatarBackground
    };
  };

  return (
    <div ref={containerRef} style={{ width: "100%", height: aspectRatio ? "auto" : "100%", maxHeight: "100%", aspectRatio: aspectRatio, alignSelf: "center", justifySelf: "center", position: "relative", display: "flex", justifyContent: "center", alignItems: "center", ...getContainerBackgroundStyle() }}>
      <video
        ref={videoRef}
        playsInline
        autoPlay
        muted
        style={
          applyGreenscreen
            ? {
              position: "absolute",
              top: 0,
              left: "-9999px",
              width: "1px",
              height: "1px",
              opacity: 0,
              pointerEvents: "none"
            }
            : {
              width: "100%",
              height: aspectRatio ? "auto" : "100%",
              maxHeight: "100%",
              aspectRatio: aspectRatio,
              objectFit: "cover",
              opacity: isPaused ? 0.3 : 1,
              transform: `scale(${avatarZoom}) translate(${resolvedTranslateX}px, ${resolvedTranslateY}px)`,
              transformOrigin: "center center",
              transition: "opacity 0.3s, transform 0.3s ease",
              display: "block"
            }
        }
      />
      {applyGreenscreen && (
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: aspectRatio ? "auto" : "100%",
            maxHeight: "100%",
            aspectRatio: aspectRatio,
            objectFit: "cover",
            opacity: isPaused ? 0.3 : 1,
            transform: `scale(${avatarZoom}) translate(${resolvedTranslateX}px, ${resolvedTranslateY}px)`,
            transformOrigin: "center center",
            transition: "opacity 0.3s, transform 0.3s ease"
          }}
        />
      )}
      <audio ref={audioRef} autoPlay playsInline style={{ display: "none" }} />

      {!loading && !isMiniPlayer && (
        <div className="video-controls-bar">
          <button
            onClick={handleToggleMute}
            className={`video-control-btn ${isMuted ? "mute-active" : ""}`}
            title={isMuted ? "Unmute Mic" : "Mute Mic"}
          >
            {isMuted ? <FiMicOff size={16} /> : <FiMic size={16} />}
          </button>

          <button
            onClick={onToggleMiniPlayer}
            className="video-control-btn"
            title="Minimize Video"
          >
            <FiTv size={16} />
          </button>

          <button
            onClick={onToggleAvatarCenter}
            className="video-control-btn"
            title={isAvatarCentered ? "Return Avatar to Widget" : "Center Avatar"}
          >
            {isAvatarCentered ? <FiMinimize size={16} /> : <FiMaximize size={16} />}
          </button>

          <button
            onClick={onEnd}
            className="video-control-btn end-btn"
            title="End Session"
          >
            <FiPhoneOff size={16} />
          </button>
        </div>
      )}

      {isMiniPlayer && (
        <div
          onClick={onToggleMiniPlayer}
          title="Expand Video"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.2s",
            zIndex: 11
          }}
        >
          <span style={{ fontSize: "1.6rem", color: "#fff", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))" }}>↗️</span>
        </div>
      )}

      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(15, 23, 42, 0.95)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            color: "#fff",
            zIndex: 9
          }}
        >
          <div className="spinner" style={{ width: "30px", height: "30px" }}></div>
          <span style={{ fontSize: "0.8rem" }}>{joiningText}</span>
        </div>
      )}

      {!loading && !isMiniPlayer && (
        <div
          style={{
            position: "absolute",
            top: "12px",
            left: "12px",
            background: "rgba(15, 23, 42, 0.65)",
            padding: "4px 8px",
            borderRadius: "12px",
            fontSize: "0.7rem",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            zIndex: 10
          }}
        >
          <span
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: avatarState === "speaking" ? "#10B981" : "#F59E0B",
              boxShadow: avatarState === "speaking" ? "0 0 8px #10B981" : "none"
            }}
          />
          {avatarState === "speaking" ? `${replicaName} Speaking` : "Listening..."}
        </div>
      )}
    </div>
  );
}
